import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useYbugApi } from 'ybug-react';
import { subscribeToErrorModal, closeErrorModal } from '../../services/errorModalService';
import { ybugSettings } from '../../services/ybugSettings';
import { useProfileStore } from '../../store/useProfileStore';
import { Modal } from './Modal';

// Codes that get the "try again / contact support" footer beneath the
// error text. Curated to the generic "something's wrong and there's
// nothing actionable for the user" set — domain-specific codes
// (STRIPE_PAYMENT_DECLINED, ORDER_INVALID_STATUS, etc.) get their own
// targeted messages and don't need the support nudge.
const SHOW_SUPPORT_FOOTER_CODES = new Set([
  'UNKNOWN',
  'REQUEST_BAD',
  'NOT_FOUND',
  'CONFLICT',
  'STALE_FETCH',
  'TARGET_INVALID',
  'RESOURCE_GONE',
]);

export const GlobalErrorModal: React.FC = () => {
  const { t } = useTranslation('errors');
  const ybug = useYbugApi();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const [state, setState] = useState({
    isOpen: false,
    errorMessage: '',
    correlationId: '',
    errorCode: '',
  });
  const [isOpeningSupport, setIsOpeningSupport] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToErrorModal(setState);
    return unsubscribe;
  }, []);

  // Only offer Contact Support when Ybug is actually wired (env flag on,
  // YbugProvider mounted). Hiding both the footer prose and the button
  // together so we never tell the user "please contact support" with no
  // way to do it.
  const canContactSupport = Boolean(ybug?.Ybug);
  const showSupportFooter =
    SHOW_SUPPORT_FOOTER_CODES.has(state.errorCode) && canContactSupport;

  const handleContactSupport = () => {
    if (!ybug?.Ybug) return;

    // Title carries the code. The comment is:
    //   1. "I received the message: '<msg>'"   (when there's a message)
    //   2. " (on /the/route)"                   (anon only, same line, when
    //                                            route is more than "/" —
    //                                            authed users get route in
    //                                            user data instead)
    //   3. blank line + "ADD DETAILS HERE.."   (always, locale-aware nudge)
    // Correlation ID rides in the user block, omitted from the comment.
    const title = state.errorCode ? `Code: ${state.errorCode}` : 'Received an error';

    const route =
      typeof window !== 'undefined'
        ? window.location.pathname + window.location.search + window.location.hash
        : '';
    const isMeaningfulRoute = route !== '' && route !== '/';

    let head = '';
    if (state.errorMessage) {
      head = t('supportReportComment', { message: state.errorMessage });
    }
    if (!activeProfile && isMeaningfulRoute) {
      const routeText = t('supportReportOnRoute', { route });
      head = head ? `${head} ${routeText}` : routeText;
    }
    const comment = head
      ? `${head}\n\n${t('supportAddDetailsPrompt')}`
      : t('supportAddDetailsPrompt');

    const language =
      activeProfile?.language ||
      (typeof navigator !== 'undefined' ? navigator.language : undefined);

    // Build the init payload step-wise so we only include user/email/name
    // when authenticated. `feedback.title` and `feedback.feedback_type` are
    // the experimental fields — typed `as any` at the boundary since
    // ybug-react's YbugSettings type doesn't expose them.
    const feedbackBlock: Record<string, unknown> = {
      comment,
      title,
      feedback_type: 'bug',
    };
    if (activeProfile) {
      feedbackBlock.email = activeProfile.email;
      feedbackBlock.name = activeProfile.displayName;
    }

    const initPayload: Record<string, unknown> = {
      ...ybugSettings,
      feedback: feedbackBlock,
    };
    if (language) initPayload.language_override = language;
    if (activeProfile) {
      // Ybug's user attributes accept arbitrary string keys; correlationId,
      // profileId, and the current route ride along here so the report links
      // back to backend logs + the user record + the screen they were on.
      // id is the profile UUID (Ybug uses it for user grouping); profileId
      // is intentionally redundant for parity with how reports get filed
      // against profiles in other tools.
      initPayload.user = {
        id: activeProfile.id,
        name: activeProfile.displayName,
        email: activeProfile.email,
        profileId: activeProfile.id,
        ...(route ? { route } : {}),
        ...(state.correlationId ? { correlationId: state.correlationId } : {}),
      };
    }

    // ybug.init() REPLACES window.ybug_settings (see comment in ybugInit.tsx).
    // We spread ybugSettings first so iOS flow directives survive, and accept
    // the type-cast cost on the experimental feedback fields.
    ybug.init(initPayload as Parameters<typeof ybug.init>[0]);
    ybug.Ybug.open('feedback');

    // Ybug's iframe takes ~a few seconds to load and render on first open.
    // Keep the error modal up with a "Opening support…" affordance during
    // that window so the user has something familiar to look at instead of
    // a moment of "did anything happen?" blank space.
    setIsOpeningSupport(true);
    setTimeout(() => {
      closeErrorModal();
      setIsOpeningSupport(false);
    }, 5000);
  };

  return (
    <Modal isOpen={state.isOpen} onClose={closeErrorModal} size="sm" zIndexClass="z-[2000]">
      <h2 className="text-lg font-bold text-foreground mb-2">Error</h2>
      <p className="text-sm text-muted-foreground mb-4 break-words whitespace-pre-wrap">
        {state.errorMessage}
      </p>
      {showSupportFooter && (
        <p className="text-sm text-muted-foreground mb-4">
          {t('supportFooter')}
        </p>
      )}
      {state.correlationId && (
        <p className="text-xs text-muted-foreground mb-4">
          ID: {state.correlationId}
        </p>
      )}
      <div className="flex items-center">
        {showSupportFooter && (
          <button
            type="button"
            onClick={handleContactSupport}
            disabled={isOpeningSupport}
            className="btn-outline px-4 py-2 text-sm font-medium text-brand-primary bg-transparent border-brand-primary rounded-lg hover:bg-brand-primary/5 transition-colors disabled:opacity-60"
          >
            {isOpeningSupport ? 'Opening support…' : 'Contact Support'}
          </button>
        )}
        <button
          onClick={closeErrorModal}
          className="ml-auto px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};
